import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import styles from "./template-editing.module.css";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import {
  BackButton,
  DeleteButton,
} from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { saveSnapshotLocally } from "PosterEditor/utils/snapshot-local";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "../../toolbox-components/toolbox-component-section";
import { ToolboxComponentCheckbox as Checkbox } from "../../toolbox-components/toolbox-component-checkbox";
import { ToolboxComponentInput as Input } from "../../toolbox-components/toolbox-component-input";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { ProjectTemplateAppliedElements } from "PosterEditor/model/project-template";
import { EditingPermissions } from "PosterEditor/model/editing-permissions";

interface TemplateEditingProps {}

export const TemplateEditing = (props: TemplateEditingProps) => {
  const { template, setTemplate, setEditingPermissions } = usePosterProject();
  const { takeSnapshot } = useEditorUI();

  const takeThumbnailSnapshot = () => {
    takeSnapshot();
  };

  const setTitle = (text: string) => {
    if (template) {
      setTemplate({ ...template, name: text });
    }
  };

  if (!template) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          <TitleAndMeta title={template.name} onChangeTitle={setTitle} />
          <Divider whitespaceOnly />
          <Section>
            <CTAButton onClick={takeThumbnailSnapshot}>
              Update thumbnail
            </CTAButton>
            <Divider whitespaceOnly />
          </Section>
          <p className={styles.description}>
            Select which elements to apply to projects created from this
            template
          </p>
          <Options
            elements={template?.appliedElements}
            onChangeElements={(elements) => {
              const newTemplate = { ...template, appliedElements: elements };
              setTemplate(newTemplate);
            }}
          />
          <Divider />

          <EditingPermissionsControls
            editingPermissions={template.editingPermissions}
            setChangePermissions={(editingPermissions) => {
              const newTemplate = { ...template, editingPermissions };
              //Set permissions on the template's project as well.
              setEditingPermissions(editingPermissions);
              setTemplate(newTemplate);
            }}
          />
        </div>
      </Toolbox>
    </div>
  );
};

interface TitleAndMetaProps {
  title: string;
  onChangeTitle: (text: string) => void;
}

const TitleAndMeta = (props: TitleAndMetaProps) => {
  return (
    <Section>
      <Input text={props.title} onChangeText={props.onChangeTitle} />
    </Section>
  );
};

interface OptionsProps {
  elements: ProjectTemplateAppliedElements;
  onChangeElements: (elements: ProjectTemplateAppliedElements) => void;
}

const Options = (props: OptionsProps) => {
  return (
    <>
      <Section title="Applied Styling">
        <Checkbox
          text="Map style"
          isSelected={props.elements.mapStyle}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              mapStyle: !props.elements.mapStyle,
            });
          }}
        />
        <Checkbox
          text="Poster size"
          isSelected={props.elements.posterSize}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              posterSize: !props.elements.posterSize,
            });
          }}
        />
        <Checkbox
          text="Border style"
          isSelected={props.elements.borderStyle}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              borderStyle: !props.elements.borderStyle,
            });
          }}
        />
        <Checkbox
          text="Point style"
          isSelected={props.elements.pointStyle}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              pointStyle: !props.elements.pointStyle,
            });
          }}
        />
        <Checkbox
          text="Line style"
          isSelected={props.elements.lineStyle}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              lineStyle: !props.elements.lineStyle,
            });
          }}
        />
        <Checkbox
          text="Map label style"
          isSelected={props.elements.mapLabelStyle}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              mapLabelStyle: !props.elements.mapLabelStyle,
            });
          }}
        />
        <Checkbox
          text="Static label style"
          isSelected={props.elements.staticLabelStyle}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              staticLabelStyle: !props.elements.staticLabelStyle,
            });
          }}
        />
        <Checkbox
          text="Backdrop style"
          isSelected={props.elements.backdropStyle}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              backdropStyle: !props.elements.backdropStyle,
            });
          }}
        />
      </Section>
      <Divider whitespaceOnly />
      <Section title="Applied Data">
        <Checkbox
          text="Route coordinates"
          isSelected={props.elements.routeCoordinates}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              routeCoordinates: !props.elements.routeCoordinates,
            });
          }}
        />
        <Checkbox
          text="Map coordinates"
          isSelected={props.elements.mapCoordinates}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              mapCoordinates: !props.elements.mapCoordinates,
            });
          }}
        />
        <Checkbox
          text="Styled points"
          isSelected={props.elements.styledPoints}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              styledPoints: !props.elements.styledPoints,
            });
          }}
        />
        <Checkbox
          text="Backdrops"
          isSelected={props.elements.backdrops}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              backdrops: !props.elements.backdrops,
            });
          }}
        />
        <Checkbox
          text="Map labels"
          isSelected={props.elements.mapLabels}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              mapLabels: !props.elements.mapLabels,
            });
          }}
        />
        <Checkbox
          text="Static labels"
          isSelected={props.elements.staticLabels}
          onClick={() => {
            props.onChangeElements({
              ...props.elements,
              staticLabels: !props.elements.staticLabels,
            });
          }}
        />
      </Section>
    </>
  );
};

interface EditingPermissionsControlsProps {
  editingPermissions: EditingPermissions;
  setChangePermissions: (editingPermissions: EditingPermissions) => void;
}

const EditingPermissionsControls = (props: EditingPermissionsControlsProps) => {
  const { editingPermissions, setChangePermissions } = props;

  if (!editingPermissions) {
    return null;
  }

  return (
    <Section title="Editing permissions">
      <Checkbox
        text="Map style"
        isSelected={editingPermissions.mapStyle}
        onClick={() =>
          setChangePermissions({
            ...editingPermissions,
            mapStyle: !editingPermissions.mapStyle,
          })
        }
      />
      <Checkbox
        text="Map pan"
        isSelected={editingPermissions.mapPan}
        onClick={() =>
          setChangePermissions({
            ...editingPermissions,
            mapPan: !editingPermissions.mapPan,
          })
        }
      />
      <Checkbox
        text="Map zoom"
        isSelected={editingPermissions.mapZoom}
        onClick={() =>
          setChangePermissions({
            ...editingPermissions,
            mapZoom: !editingPermissions.mapZoom,
          })
        }
      />
      <Checkbox
        text="GPS source"
        isSelected={editingPermissions.gpsSource}
        onClick={() =>
          setChangePermissions({
            ...editingPermissions,
            gpsSource: !editingPermissions.gpsSource,
          })
        }
      />
      <Checkbox
        text="Poster size"
        isSelected={editingPermissions.posterSize}
        onClick={() =>
          setChangePermissions({
            ...editingPermissions,
            posterSize: !editingPermissions.posterSize,
          })
        }
      />

      <Section title="Line">
        {Object.entries(editingPermissions.line).map(([key, value]) => (
          <Checkbox
            key={`line-${key}`}
            text={`Line ${key}`}
            isSelected={value}
            onClick={() =>
              setChangePermissions({
                ...editingPermissions,
                line: {
                  ...editingPermissions.line,
                  [key]: !value,
                },
              })
            }
          />
        ))}
      </Section>

      <Section title="Point">
        {Object.entries(editingPermissions.point).map(([key, value]) => (
          <Checkbox
            key={`point-${key}`}
            text={`Point ${key}`}
            isSelected={value}
            onClick={() =>
              setChangePermissions({
                ...editingPermissions,
                point: {
                  ...editingPermissions.point,
                  [key]: !value,
                },
              })
            }
          />
        ))}
      </Section>

      <Section title="Border">
        {Object.entries(editingPermissions.border).map(([key, value]) => (
          <Checkbox
            key={`border-${key}`}
            text={`Border ${key}`}
            isSelected={value}
            onClick={() =>
              setChangePermissions({
                ...editingPermissions,
                border: {
                  ...editingPermissions.border,
                  [key]: !value,
                },
              })
            }
          />
        ))}
      </Section>

      <Section title="Map label">
        {Object.entries(editingPermissions.mapLabel).map(([key, value]) => (
          <Checkbox
            key={`mapLabel-${key}`}
            text={`Map label ${key}`}
            isSelected={value}
            onClick={() =>
              setChangePermissions({
                ...editingPermissions,
                mapLabel: {
                  ...editingPermissions.mapLabel,
                  [key]: !value,
                },
              })
            }
          />
        ))}
      </Section>

      <Section title="Static label">
        {Object.entries(editingPermissions.staticLabel).map(([key, value]) => (
          <Checkbox
            key={`staticLabel-${key}`}
            text={`Static label ${key}`}
            isSelected={value}
            onClick={() =>
              setChangePermissions({
                ...editingPermissions,
                staticLabel: {
                  ...editingPermissions.staticLabel,
                  [key]: !value,
                },
              })
            }
          />
        ))}
      </Section>

      <Section title="Backdrop">
        {Object.entries(editingPermissions.backdrop).map(([key, value]) => (
          <Checkbox
            key={`backdrop-${key}`}
            text={`Backdrop ${key}`}
            isSelected={value}
            onClick={() =>
              setChangePermissions({
                ...editingPermissions,
                backdrop: {
                  ...editingPermissions.backdrop,
                  [key]: !value,
                },
              })
            }
          />
        ))}
      </Section>
    </Section>
  );
};
